import graphqlClient from "@/GQLClient";
import type {
  AuditLogFragment,
  PlannerEntity,
  PlannerTaskFragment,
} from "@/graphql";
import {
  DeletePlannerTemplateDocument,
  GetPlannerCategoriesDocument,
  GetPlannerTemplatesDocument,
  SavePlannerCategoryDocument,
  SavePlannerTemplateDocument,
  type PlannerCategoryInput,
  GetPlannerFiltersDocument,
  type PlannerFilterInput,
  SavePlannerFilterDocument,
  DeletePlannerFilterDocument,
  type PlannerTemplateInput,
  AuditLogQueryDocument,
  Audit_Event_Entity,
  GetPlannerTasksDocument,
  GetSinglePlannerTaskDocument,
  GetPlannerSubTasksDocument,
  CompletePlannerSubtaskDocument,
  UncompletePlannerSubtaskDocument,
  GetPlannerCategoryStatsDocument,
  DeletePlannerCategoryDocument,
  CompletePlannerTaskDocument,
  UncompletePlannerTaskDocument,
  EditPlannerTaskDocument,
  type PlannerTaskEditInput,
  GetMeetingEntitiesDocument,
  GetMealsEntitiesDocument,
  GetActivityEntitiesDocument,
  GetSlottedBookingResourcesDocument,
  type DynamicBookingSlotFieldsFragment,
  DynamicBookingSlotFieldsFragmentDoc,
  GetBookingEntitiesDocument,
  CreatePlannerTaskDocument,
  SearchPlannerTasksDocument,
  type PlannerTaskCreateInput,
  DeleteTaskDocument,
  Planner_Task_Delete_Type,
} from "@/graphql/generated-types";
import SentryGraphQLError from "@/helpers/exceptions/SentryGraphQLError";
import { useAuthStore } from "@/stores/authStore";
import type { PlannerModuleFilterType, NewFilterTypes } from "@/types";
import type { TypedDocumentNode } from "@apollo/client";
import { format, formatISO } from "date-fns";
import { print } from "graphql";
import gql from "graphql-tag";

const DYNAMIC_BOOKING_SLOTS_FIELDS = print(DynamicBookingSlotFieldsFragmentDoc);

type EntityQueryType =
  | typeof GetMeetingEntitiesDocument
  | typeof GetMealsEntitiesDocument
  | typeof GetActivityEntitiesDocument
  | typeof GetBookingEntitiesDocument;

const ENTITY_QUERY_MAP: Record<PlannerModuleFilterType, EntityQueryType> = {
  meeting: GetMeetingEntitiesDocument,
  booking: GetBookingEntitiesDocument,
  meal: GetMealsEntitiesDocument,
  activity: GetActivityEntitiesDocument,
};

const getCustomFilters = async () => {
  const response = await graphqlClient.query({
    query: GetPlannerFiltersDocument,
  });

  if (response.data.planner?.getFilters) {
    return response.data.planner?.getFilters as NewFilterTypes[];
  }

  throw new SentryGraphQLError();
};

const getCategories = async () => {
  const response = await graphqlClient.query({
    query: GetPlannerCategoriesDocument,
  });

  if (response.data.planner?.getCategories) {
    return response.data.planner?.getCategories;
  }

  throw new SentryGraphQLError();
};

const getCategoryCountInUse = async (id: string) => {
  const response = await graphqlClient.query({
    query: GetPlannerCategoryStatsDocument,
    variables: {
      ids: [id],
    },
  });

  if (response.data.planner?.getCategories) {
    const { getCategories } = response.data.planner;

    if (getCategories.length > 0) {
      const [item] = response.data.planner.getCategories;
      return (
        (item.tasks?.totalCount || 0) + (item.repeatTasks?.totalCount || 0)
      );
    }

    return 0;
  }

  throw new SentryGraphQLError();
};

const saveCategory = async (category: PlannerCategoryInput) => {
  const authStore = useAuthStore();
  category.institution = authStore.institutionId;

  const response = await graphqlClient.mutate({
    mutation: SavePlannerCategoryDocument,
    variables: {
      category,
    },
  });

  if (response.data?.planner?.saveCategory?.success) {
    return response.data.planner.saveCategory;
  }

  throw new SentryGraphQLError();
};

const deleteCategory = async (id: string) => {
  const response = await graphqlClient.mutate({
    mutation: DeletePlannerCategoryDocument,
    variables: {
      id,
    },
  });

  if (response.data?.planner?.deleteCategory?.success) {
    return response.data.planner.deleteCategory;
  }

  throw new SentryGraphQLError();
};

const saveFilter = async (filter: PlannerFilterInput) => {
  const authStore = useAuthStore();
  filter.institution = authStore.institutionId;

  const response = await graphqlClient.mutate({
    mutation: SavePlannerFilterDocument,
    variables: {
      filter,
    },
  });

  if (response.data?.planner?.saveFilter?.success) {
    return response.data.planner.saveFilter;
  }

  throw new SentryGraphQLError();
};

const deleteFilter = async (id: string) => {
  const response = await graphqlClient.mutate({
    mutation: DeletePlannerFilterDocument,
    variables: {
      id,
    },
  });

  if (response.data?.planner?.deleteFilter?.success) {
    return response.data.planner.deleteFilter;
  }

  throw new SentryGraphQLError();
};

const getPlannerTemplates = async () => {
  const response = await graphqlClient.query({
    query: GetPlannerTemplatesDocument,
  });

  if (response.data.plannerTemplates) {
    return response.data.plannerTemplates;
  }

  throw new SentryGraphQLError();
};

const deletePlannerTemplates = async (id: string) => {
  const response = await graphqlClient.mutate({
    mutation: DeletePlannerTemplateDocument,
    variables: {
      id,
    },
  });

  if (response.data?.planner?.deleteTemplate?.success) {
    return response.data.planner.deleteTemplate;
  }

  throw new SentryGraphQLError();
};

const saveTemplate = async (template: PlannerTemplateInput) => {
  const response = await graphqlClient.mutate({
    mutation: SavePlannerTemplateDocument,
    variables: {
      template,
    },
  });

  if (response.data?.planner?.saveTemplate?.success) {
    return response.data.planner.saveTemplate;
  }

  throw new SentryGraphQLError();
};

const getAuditLog = async (
  entityId: string[],
  entityType: Audit_Event_Entity,
): Promise<AuditLogFragment[]> => {
  const response = await graphqlClient.query({
    query: AuditLogQueryDocument,
    variables: {
      filters: { entityId, entityType },
    },
  });

  if (response.data.auditLog?.getByFilters) {
    return response.data.auditLog?.getByFilters;
  }

  throw new SentryGraphQLError();
};

const completePlannerSubtask = async (
  plannerTaskId: string,
  plannerSubtaskId: string,
) => {
  const response = await graphqlClient.mutate({
    mutation: CompletePlannerSubtaskDocument,
    variables: {
      plannerTaskId,
      plannerSubtaskId,
    },
  });

  if (response.data?.completePlannerSubtask?.success) {
    return response.data.completePlannerSubtask;
  }

  throw new SentryGraphQLError();
};

const uncompletePlannerSubtask = async (
  plannerTaskId: string,
  plannerSubtaskId: string,
) => {
  const response = await graphqlClient.mutate({
    mutation: UncompletePlannerSubtaskDocument,
    variables: {
      plannerTaskId,
      plannerSubtaskId,
    },
  });

  if (response.data?.uncompletePlannerSubtask?.success) {
    return response.data.uncompletePlannerSubtask;
  }

  throw new SentryGraphQLError();
};

const createTask = async (input: PlannerTaskCreateInput) => {
  const response = await graphqlClient.mutate({
    mutation: CreatePlannerTaskDocument,
    variables: {
      plannerTask: input,
    },
  });

  if (response.data?.createPlannerTask?.success) {
    return response.data.createPlannerTask;
  }

  throw new SentryGraphQLError();
};

const deleteTask = async (
  id: string,
  deleteType: Planner_Task_Delete_Type,
  deleteCompleted: boolean,
) => {
  const response = await graphqlClient.mutate({
    mutation: DeleteTaskDocument,
    variables: {
      input: {
        id,
        deleteType,
        deleteCompleted,
      },
    },
  });

  if (response.data?.deletePlannerTask?.success) {
    return response.data.deletePlannerTask;
  }

  throw new SentryGraphQLError();
};

const getPlannerTasks = async (dateFrom: string, dateTo: string) => {
  const response = await graphqlClient.query({
    query: GetPlannerTasksDocument,
    variables: {
      dateFrom,
      dateTo,
    },
  });

  if (response.data.planner?.getTasksByFilters) {
    return response.data.planner?.getTasksByFilters as PlannerTaskFragment[];
  }

  throw new SentryGraphQLError();
};

const searchPlannerTasks = async (query: string) => {
  const response = await graphqlClient.query({
    query: SearchPlannerTasksDocument,
    variables: {
      query,
    },
  });

  if (response.data.planner?.search) {
    return response.data.planner?.search;
  }

  return [];
};

const getSinglePlannerTask = async (id: string) => {
  const response = await graphqlClient.query({
    query: GetSinglePlannerTaskDocument,
    variables: {
      id,
      includeNotificationSettings: true,
    },
  });

  if (response.data.plannerTaskEntry) {
    return response.data.plannerTaskEntry as PlannerTaskFragment;
  }

  throw new SentryGraphQLError();
};

const getSubtasks = async (id: string) => {
  const response = await graphqlClient.query({
    query: GetPlannerSubTasksDocument,
    variables: {
      id,
    },
  });

  if (response.data.plannerTaskEntry) {
    return response.data.plannerTaskEntry;
  }
};

const completePlannerTask = async (plannerTaskId: string) => {
  const response = await graphqlClient.mutate({
    mutation: CompletePlannerTaskDocument,
    variables: {
      plannerTaskId,
    },
  });

  if (response.data?.completePlannerTask?.success) {
    return response.data.completePlannerTask;
  }

  throw new SentryGraphQLError();
};

const uncompletePlannerTask = async (plannerTaskId: string) => {
  const response = await graphqlClient.mutate({
    mutation: UncompletePlannerTaskDocument,
    variables: {
      plannerTaskId,
    },
  });

  if (response.data?.uncompletePlannerTask?.success) {
    return response.data.uncompletePlannerTask;
  }
};

const createPlannerTask = async (plannerTask: PlannerTaskCreateInput) => {
  const response = await graphqlClient.mutate({
    mutation: CreatePlannerTaskDocument,
    variables: {
      plannerTask,
    },
  });

  if (response.data?.createPlannerTask?.success) {
    return response.data.createPlannerTask;
  }

  throw new SentryGraphQLError();
};

const editPlannerTask = async (
  plannerTaskId: string,
  plannerTask: PlannerTaskEditInput,
) => {
  const response = await graphqlClient.mutate({
    mutation: EditPlannerTaskDocument,
    variables: {
      plannerTaskId,
      plannerTask,
    },
  });

  if (response.data?.editPlannerTask?.success) {
    return response.data.editPlannerTask;
  }

  throw new SentryGraphQLError();
};

const getSlottedBookingResources = async () => {
  const response = await graphqlClient.query({
    query: GetSlottedBookingResourcesDocument,
  });

  if (response.data.institutions?.getMyInstitution.bookingResources?.edges) {
    return response.data.institutions.getMyInstitution.bookingResources.edges;
  }

  throw new SentryGraphQLError();
};

const getBookingSlotsEntities = async (startAt: Date, endAt: Date) => {
  const slottedBookingResources = await getSlottedBookingResources();
  const resources = (slottedBookingResources || []).map((item) => {
    if (
      item.node.__typename === "BookingResource" &&
      item.node.slotted &&
      item.node.id
    ) {
      return item.node;
    }

    return undefined;
  });

  const resourceIds: string[] = resources
    .filter((item): item is NonNullable<typeof item> => !!item?.id)
    .map((item) => item.id);

  if (resourceIds.length === 0) {
    return [];
  }

  // Dynamically build query with aliases
  const aliasedFields = resourceIds
    .map(
      (id) => `
      resource${id}: bookingSlots(
        bookingResourceId: "${id}"
        dateFrom: $dateFrom
        dateTo: $dateTo
        timezone: $timezone
      ) {
        ...dynamicBookingSlotFields
      }
    `,
    )
    .join("\n");

  type MultipleBookingSlotsResult = Record<
    string,
    DynamicBookingSlotFieldsFragment[]
  >;

  type Params = {
    dateFrom: string;
    dateTo: string;
    timezone: string;
  };

  // We should generate a dynamic query. But we should keep the
  // getDynamicBookingSlotsDocument.graphql file to have a return type
  const dynamicQuery = gql`
    ${DYNAMIC_BOOKING_SLOTS_FIELDS}
    query getDynamicBookingSlots (
      $dateFrom: Date!
      $dateTo: Date!
      $timezone: TimeZone!
    ) {
      ${aliasedFields}
    }
  ` as TypedDocumentNode<MultipleBookingSlotsResult, Params>;

  const response = await graphqlClient.query({
    query: dynamicQuery,
    variables: {
      dateFrom: format(startAt, "yyyy-MM-dd"),
      dateTo: format(endAt, "yyyy-MM-dd"),
      timezone: "Europe/Copenhagen",
    },
  });

  if (!response.data) {
    throw new SentryGraphQLError();
  }

  const result: PlannerEntity[] = [];

  Object.keys(response.data).forEach((resourceKey) => {
    const slots = response.data[resourceKey];
    const resourceId = resourceKey.replace("resource", "");

    slots
      .filter((slot) => slot.participants && slot.participants.length > 0)
      .forEach((slot) => {
        result.push({
          __typename: "BookingResource",
          id: resourceId + "|" + slot.id,
          startDate: slot.startDate,
          endDate: slot.endDate,
          participants: slot.participants as PlannerEntity["participants"],
          title: slot.description || "",
          description: null,
          departments: null,
        });
      });
  });

  return result;
};

const runPlannerEntityQuery = async (
  query: EntityQueryType,
  dateFrom: string,
  dateTo: string,
) => {
  const response = await graphqlClient.query({
    query,
    variables: {
      filter: {
        dateFrom,
        dateTo,
      },
    },
  });

  if (response.data.institutions?.getMyInstitution.result?.edges) {
    return response.data.institutions.getMyInstitution.result.edges.map(
      (item) => item.node as PlannerEntity,
    );
  }

  throw new SentryGraphQLError();
};

const runPlannerBookingEntityQuery = async (
  dateFrom: string,
  dateTo: string,
) => {
  const response = await graphqlClient.query({
    query: GetBookingEntitiesDocument,
    variables: {
      filter: {
        dateFrom,
        dateTo,
      },
    },
  });

  if (response.data.institutions?.getMyInstitution.result?.edges) {
    return response.data.institutions.getMyInstitution.result.edges.map(
      (item) => {
        let title =
          item.node.__typename === "BookingSlot" ? item.node.title : undefined;

        if (
          (!title || title.trim().length === 0) &&
          item.node.__typename === "BookingSlot"
        ) {
          title = item.node.resource.title;
        }

        return {
          ...item.node,
          title,
        } as PlannerEntity;
      },
    );
  }

  throw new SentryGraphQLError();
};

const getPlannerEntities = async (
  type: PlannerModuleFilterType,
  startAt: Date,
  endAt: Date,
): Promise<PlannerEntity[]> => {
  // We might call multiple queries at the same time
  const promises = [];

  if (type === "booking") {
    // wWe should get booking slots
    promises.push(getBookingSlotsEntities(startAt, endAt));
  }

  const dateFrom = formatISO(startAt);
  const dateTo = formatISO(endAt);

  const query = ENTITY_QUERY_MAP[type];

  if (!query) {
    throw new Error(`Undefined query type: ${type}`);
  }

  // Booking queries has special condition
  if (type === "booking") {
    promises.push(runPlannerBookingEntityQuery(dateFrom, dateTo));
  } else {
    promises.push(runPlannerEntityQuery(query, dateFrom, dateTo));
  }

  return (await Promise.all(promises)).flat();
};

export default {
  // Categories
  getCategories,
  getCategoryCountInUse,
  saveCategory,
  deleteCategory,

  // Filters
  getCustomFilters,
  saveFilter,
  deleteFilter,

  // Templates
  getPlannerTemplates,
  deletePlannerTemplates,
  saveTemplate,

  // Audit
  getAuditLog,

  // Tasks
  createTask,
  deleteTask,
  getPlannerTasks,
  searchPlannerTasks,
  getSinglePlannerTask,
  completePlannerTask,
  uncompletePlannerTask,
  createPlannerTask,
  editPlannerTask,
  getPlannerEntities,
  getSlottedBookingResources,

  // Subtasks
  getSubtasks,
  completePlannerSubtask,
  uncompletePlannerSubtask,
};
